import { useEffect, useState } from 'react'

import { Box, Button, Divider } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useFormContext } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type { LocationNodes } from 'modules/locations/hooks/useLocations'
import type { WorkAreaNodes } from 'modules/locations/hooks/useWorkAreas'

interface filterFormFields {
  startTime: string
  endTime: string
  locationId: string
  workAreaId: string
  date: string
}

interface Props {
  locations: LocationNodes
  workAreas: WorkAreaNodes
}

const WorkSpaceFilter = ({ locations, workAreas }: Props) => {
  const currentUser = users.current
  const filterForm = useFormContext<filterFormFields>()
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null)

  useEffect(() => {
    filterForm.setFieldValue('workAreaId', '')
  }, [selectedLocation])

  const handleReset = () => {
    filterForm.setValues({
      startTime: '',
      endTime: '',
      locationId: '',
      workAreaId: '',
      date: dateToString(moment(), 'backend'),
    })
    track(trackingEvents.spaceControl.bookings.filterReset, {})
    filterForm.handleSubmit()
  }
  const locationsOptions = locations.map((location) => ({
    label: location.name,
    value: location.id,
  }))

  const workAreasOptions = workAreas
    .map((workArea) => ({
      label: `${workArea.location?.name} - ${workArea.name}`,
      value: workArea.id,
      locationId: workArea.location?.id,
    }))
    .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label))

  const handleApply = () => {
    const location = locationsOptions.find(() => filterForm.values.locationId)?.label
    const workArea = workAreasOptions.find(() => filterForm.values.workAreaId)?.label
    track(trackingEvents.spaceControl.bookings.filterAvailability, {
      day: filterForm.values.date,
      startTime: filterForm.values.startTime === '' ? null : filterForm.values.startTime,
      endTime: filterForm.values.endTime === '' ? null : filterForm.values.endTime,
      location: location ?? null,
      workArea: workArea ?? null,
    })
    filterForm.handleSubmit()
  }

  return (
    <Box
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
    >
      <DayPicker
        minDate={moment().startOf('day')}
        value={filterForm.values.date}
        onChange={(value) => {
          const formatted = stringToDate(value, 'ui')
          filterForm.setFieldValue('date', formatted)
        }}
        mandatory
        showOnlyCalendar
        firstDayOfWeek={firstDayOfWeekAsIndex(currentUser.attributes.first_day_of_week)}
      />
      <Box gap="s16" padding="s24">
        <Divider />
        <Box flexDirection="row" gap="s16">
          <TextField
            type="time"
            label={i18n.t('space_control.overview.filters.start_time')}
            value={filterForm.values.startTime}
            onChange={(value) => filterForm.setValues({ ...filterForm.values, startTime: value })}
            error={filterForm.errors.startTime}
          />
          <TextField
            type="time"
            label={i18n.t('space_control.overview.filters.end_time')}
            value={filterForm.values.endTime}
            onChange={(value) => filterForm.setValues({ ...filterForm.values, endTime: value })}
            error={filterForm.errors.endTime}
          />
        </Box>
        <Select
          none={i18n.t('space_control.overview.filters.all_workplaces')}
          label={i18n.t('space_control.overview.filters.location')}
          options={locationsOptions}
          value={filterForm.values.locationId}
          onChange={(locationId: string) => {
            setSelectedLocation(+locationId)
            filterForm.setFieldValue('locationId', locationId)
          }}
        />
        <Select
          none={i18n.t('space_control.overview.filters.all_workareas')}
          label={i18n.t('space_control.overview.filters.workarea')}
          options={
            selectedLocation
              ? workAreasOptions.filter((workArea) => workArea.locationId === selectedLocation)
              : workAreasOptions
          }
          value={filterForm.values.workAreaId}
          onChange={(workAreaId: string) => {
            filterForm.setFieldValue('workAreaId', workAreaId)
          }}
        />
      </Box>
      <Box
        flexDirection="row"
        justifyContent="flexEnd"
        gap="s8"
        background="grey100"
        paddingTop="s16"
        paddingBottom="s24"
        paddingRight="s24"
        border={{
          top: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        <Button type="secondary" disabled={false} onClick={handleReset}>
          {i18n.t('space_control.overview.filters.reset')}
        </Button>
        <Button onClick={handleApply}>{i18n.t('space_control.overview.filters.apply')}</Button>
      </Box>
    </Box>
  )
}

export default observer(WorkSpaceFilter)
